import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Employee, Dependent } from "@shared/schema";
import { DEPENDENT_RELATIONSHIP_OPTIONS } from "@shared/constants/dependentRelationships";
import { toast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { Users, Plus, Trash2, Edit } from "lucide-react";
import { formatDateSafe } from "@/lib/utils";

const dependentSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  middleInitial: z.string().max(5).optional(),
  lastName: z.string().min(1, "Last name is required"),
  relationship: z.string().min(1, "Relationship is required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  sex: z.string().min(1, "Sex is required"),
  ssn: z.string().optional(),
  isStudent: z.boolean().optional(),
  isDisabled: z.boolean().optional(),
});

type DependentFormData = z.infer<typeof dependentSchema>;

interface DependentsSetupScreenProps {
  employee: Employee;
  dependents: Dependent[];
  onComplete: () => void;
  onSkip: () => void;
}

export function DependentsSetupScreen({
  employee,
  dependents,
  onComplete,
  onSkip,
}: DependentsSetupScreenProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(
    null
  );

  const form = useForm<DependentFormData>({
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      firstName: "",
      middleInitial: "",
      lastName: "",
      relationship: "",
      dateOfBirth: "",
      sex: "",
      ssn: "",
      isStudent: false,
      isDisabled: false,
    },
  });

  const createDependentMutation = useMutation({
    mutationFn: async (data: DependentFormData) => {
      const response = await fetch(`/api/employees/me/dependents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to create dependent");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/employees/me/dependents"],
      });
      toast({
        title: "Dependent added",
        description: "The dependent has been added successfully.",
        variant: "success",
      });
      form.reset();
      setIsAddDialogOpen(false);
    },
    onError: () => {
      toast({
        title: "Failed to add dependent",
        variant: "destructive",
      });
    },
  });

  const deleteDependentMutation = useMutation({
    mutationFn: async (dependentId: number) => {
      const response = await fetch(
        `/api/dependents/${dependentId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete dependent");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/employees/me/dependents"],
      });
      toast({
        title: "Dependent removed",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to remove dependent",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: DependentFormData) => {
    createDependentMutation.mutate(data);
  };

  const handleDelete = (dependentId: number) => {
    if (
      confirm(
        "Are you sure you want to remove this dependent? This action cannot be undone."
      )
    ) {
      deleteDependentMutation.mutate(dependentId);
    }
  };

  const calculateAge = (dateOfBirth: string | Date | null | undefined) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Users className="h-12 w-12 mx-auto text-primary mb-2" />
        <p className="text-muted-foreground">
          Add family members who may be eligible for benefit coverage.
        </p>
      </div>

      {/* Dependents List */}
      {dependents.length > 0 ? (
        <div className="space-y-3">
          {dependents.map((dependent) => {
            const age = calculateAge(dependent.dateOfBirth);
            return (
              <Card key={dependent.id} className="border">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">
                          {dependent.firstName} {dependent.lastName}
                        </h4>
                        <Badge variant="outline">
                          {dependent.relationship}
                        </Badge>
                        {dependent.isStudent && (
                          <Badge variant="secondary">Student</Badge>
                        )}
                        {dependent.isDisabled && (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-0.5">
                        {dependent.dateOfBirth && (
                          <p>
                            Date of Birth: {formatDateSafe(dependent.dateOfBirth)}
                            {age !== null && ` (Age ${age})`}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(dependent.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="pt-6 pb-6 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground mb-4">
              No dependents added yet.
            </p>
            <p className="text-sm text-muted-foreground">
              Add your spouse, children, or other eligible family members.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add Dependent Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogTrigger asChild>
          <Button className="w-full" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Dependent
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Dependent</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-[1fr_5rem_1fr] gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="middleInitial"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MI</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          maxLength={5}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="relationship"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Relationship *</FormLabel>
                    <Controller
                      name="relationship"
                      control={form.control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ""}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {DEPENDENT_RELATIONSHIP_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Birth *</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sex"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sex *</FormLabel>
                      <Controller
                        name="sex"
                        control={form.control}
                        render={({ field }) => (
                          <Select
                            value={field.value || ""}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select sex" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="ssn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SSN (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="XXX-XX-XXXX"
                        type="password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isDisabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-0.5 leading-none">
                      <FormLabel>Disabled dependent</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        May remain on the plan past age 26.
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createDependentMutation.isPending}
                  className="flex-1"
                >
                  {createDependentMutation.isPending ? "Adding..." : "Add Dependent"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          You can add dependents now or skip this step and add them later when
          enrolling in benefits. Dependent eligibility varies by plan type.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onSkip}>
          Skip for Now
        </Button>
        <Button onClick={onComplete}>
          Complete Onboarding
        </Button>
      </div>
    </div>
  );
}
